import * as React from "react";
import { Link } from "react-router-dom";
import * as api from "app/shared/api";
import { useDispatch, useSelector } from "react-redux";
import {
  Table,
  Alert,
  Modal,
  Form,
  Button,
  Dropdown,
  Spin,
  Checkbox,
  DatePicker,
  Input,
  message,
  Space,
  Select,
  Typography,
} from "antd";
import { BarsOutlined } from "@ant-design/icons";
import {
  getSelectedPricingYear,
  getSelectedProductType,
  getSelectedCentre,
  getCentres,
  getPricingYears,
  getPricelist,
  getFormState,
  getDraftPricelist,
  getIsLoading,
  getFormErrors,
  getErrorMessage,
  getSchools,
} from "../store/selectors";
import {
  BookingSystem,
  PricingYearId,
} from "app/shared/model";
import { ProductId } from "app/shared/model/EditProductOffering";
import { FormError } from "app/shared/model/Form";
import {
  loadDraftPricelist,
  loadPricelist,
  setFormErrors,
  setFormState,
  setIsLoading,
  updateDraftPricelistItem,
} from "../store/actions";
import { SaveState } from "app/shared/ui/GenericSaveAndBackButton";
import {
  DateStrategyAssign,
  EditPricelistResponse,
  OfferingType,
  SchoolOfferingStatus,
  SchoolStatusDto,
  PriceBands,
  PricePeriods,
  StandardOfferingDto,
  PackageOfferingDto,
  DecommissionProductOffering,
  RecommissionProductOffering,
} from "app/shared/model/EditPricelist";
import DateStrategyList from "./DateStrategyList";
import DateHelper from "app/helpers/dateHelper";
import dayjs from "dayjs";
import type { Dayjs } from "dayjs";
import {
  getDivisionId,
  toSchoolIdNumber,
} from "../domain/storage/localStorage";
import { isPackageOffering, isNonFeeOffering } from "../domain/offering/guards";
import {
  getCentreCurrencyIso, normIso,
} from "../domain/currency/normalizeCurrency";
import { getEnabledCurrencies } from "../domain/currency/enabledCurrencies";
import { buildNonFeeOfferingKey, sameProductId } from "../domain/offering/keys";
import { normalizeDateValue, toIso } from "../domain/date/iso";
import type { NonFeeOfferingDto } from "../domain/tables/types";
import type { FlatRow } from "../domain/tables/types";
import { buildProductFlatRows } from "../domain/tables/buildProductFlatRows";
import { getProductRowClassName } from "../domain/tables/getProductRowClassName";
import { createProductTableColumns } from "../domain/tables/createProductTableColumns";
import { FormErrorsProvider } from "app/shared/components/Form/FormErrorsContext";
import { EditModeProvider, useIsOfferingEditing } from "../domain/tables/EditModeContext";
import type { EditModeContextValue } from "../domain/tables/EditModeContext";
import { BreakdownLegendLine } from "./breakdowns/BreakdownLegendLine";
import { BreakdownsModal } from "./breakdowns/BreakdownsModal";
import { useBreakdownsModal } from "../hooks/useBreakdownsModal";
import {createNewNonFeeOffering} from "app/pages/PricelistNew/domain/offering/factories";
import {
  insertPriceBand,
  normalizePriceBands,
} from "../domain/priceBands/priceBandsNormalize";
import {
  isPriceBandFieldErrorName,
  validatePriceBandsForPeriod,
} from "../domain/validation/priceBandValidation";

const { Text } = Typography;

const EMPTY_FORM_ERRORS: FormError[] = [];

const ContextMenuTrigger: React.FC<{
  hasPendingChanges: boolean;
  allowDelete: boolean;
  offeringKey: string;
  productId: ProductId;
  isPricePeriod: boolean;
  ppi: number;
  pbi: number;
  onAction: (
    action: string,
    productId: ProductId,
    isPricePeriod: boolean,
    pricePeriodIndex: number,
    priceBandIndex: number
  ) => void;
}> = ({
  hasPendingChanges,
  allowDelete,
  offeringKey,
  productId,
  isPricePeriod,
  ppi,
  pbi,
  onAction,
}) => {
  const offeringEditing = useIsOfferingEditing(offeringKey);
  const allowContextMenu = hasPendingChanges === false && offeringEditing;

  const items: any[] = [
    {
      key: "AddRowAbove",
      label: "Add Row Above",
      onClick: () => onAction("AddRowAbove", productId, isPricePeriod, ppi, pbi),
    },
    {
      key: "AddRowBelow",
      label: "Add Row Below",
      onClick: () => onAction("AddRowBelow", productId, isPricePeriod, ppi, pbi),
    },
  ];

  if (allowDelete) {
    items.push({
      key: "DeleteRow",
      label: "Delete Row",
      onClick: () => onAction("DeleteRow", productId, isPricePeriod, ppi, pbi),
    });
  }

  return (
    <Dropdown menu={{ items }} trigger={["click"]} disabled={!allowContextMenu}>
      <Button
        type="text"
        icon={<BarsOutlined />}
        onClick={(e) => e.stopPropagation()}
      />
    </Dropdown>
  );
};

function excludeBookingSystemIdsEqual(a: string[], b: string[]): boolean {
  if (a.length !== b.length) return false;
  const sa = [...a].map(String).sort();
  const sb = [...b].map(String).sort();
  return sa.every((v, i) => v === sb[i]);
}

export interface ProductTableHandle {
  beginAdd: () => void;
}

interface ProductTableProps {
  isDraftPricelist?: boolean;
  editMode?: boolean;
  pricingYearsOverride?: PricingYearId[];
  selectedSchoolId?: number;
  showInactiveOnly?: boolean;
  queueSchoolOp?: (params: {
    op: "create" | "recommission" | "decommission" | null;
    offering: NonFeeOfferingDto;
    schoolId: number;
  }) => void;
  rowHighlightColors?: Record<string, string>;
  onRowHighlightChange?: (offeringKey: string, color: string | null) => void;
  allowedStartDate: string;
  allowedEndDate: string;
  reloadPricelist?: () => void;
}

const INT_PROPS = new Set([
  "minUnits",
  "maxUnits",
  "priceBandStrategyId",
  "strategyTypeId",
]);

const coerceIntFieldValue = (prop: string, value: any): any => {
  if (!INT_PROPS.has(prop)) return value;
  if (value === "" || value === null || value === undefined) return value;
  const n = Number(value);
  return Number.isFinite(n) ? n : value;
};

const isSameValue = (prop: string, prev: any, next: any): boolean => {
  if (prop === "startDate" || prop === "endDate" || prop.endsWith("Date")) {
    return normalizeDateValue(prev) === normalizeDateValue(next);
  }

  if (INT_PROPS.has(prop)) {
    const a =
      prev === "" || prev === null || prev === undefined ? null : Number(prev);
    const b =
      next === "" || next === null || next === undefined ? null : Number(next);
    return a === b;
  }

  const decProps = new Set(["pricePerMajorUnit", "pricePerMinorUnit"]);
  if (decProps.has(prop)) {
    const a =
      prev === "" || prev === null || prev === undefined ? null : Number(prev);
    const b =
      next === "" || next === null || next === undefined ? null : Number(next);
    return a === b;
  }

  return JSON.stringify(prev) === JSON.stringify(next);
};

const ProductTable = React.forwardRef<ProductTableHandle, ProductTableProps>(
  (props, ref) => {
    const dispatch = useDispatch<any>();
    const schools = useSelector(getSchools);
    const centres = useSelector(getCentres);
    const pricingYearsFromRedux = useSelector(getPricingYears);
    const pricingYears = props.pricingYearsOverride ?? pricingYearsFromRedux;
    const pricelist = useSelector(getPricelist);
    const draftPricelist = useSelector(getDraftPricelist);
    const selectedProductType = useSelector(getSelectedProductType);
    const selectedPricingYear = useSelector(getSelectedPricingYear);
    const selectedCentreCode = useSelector(getSelectedCentre);
    const formState = useSelector(getFormState);
    const isLoading = useSelector(getIsLoading);
    const formErrors = useSelector(getFormErrors);
    const errorMessage = useSelector(getErrorMessage);

    const editMode = !!props.editMode;
    const [editingOfferingKey, setEditingOfferingKey] = React.useState<
      string | null
    >(null);

    React.useEffect(() => {
      if (!editMode) {
        setEditingOfferingKey(null);
      }
    }, [editMode]);

    const editModeContextValue = React.useMemo<EditModeContextValue>(
      () => ({
        editMode,
        editingOfferingKey,
        setEditingOfferingKey,
        isOfferingEditing: (offeringKey: string) =>
          editMode && editingOfferingKey === offeringKey,
      }),
      [editMode, editingOfferingKey]
    );

    const draftPricelistRef = React.useRef(draftPricelist);
    const formStateRef = React.useRef(formState);
    const formErrorsRef = React.useRef(formErrors);
    draftPricelistRef.current = draftPricelist;
    formStateRef.current = formState;
    formErrorsRef.current = formErrors;

    const [showModal, setShowModal] = React.useState<boolean>(false);
    const [dateStrategies, setDateStrategies] = React.useState<DateStrategyAssign[]>([]);
    const [selectedProduct, setSelectedProduct] = React.useState<NonFeeOfferingDto | undefined>(undefined);
    const [pendingChanges, setPendingChanges] = React.useState<boolean>(false);
    const [addModalVisible, setAddModalVisible] = React.useState<boolean>(false);
    const [availableProducts, setAvailableProducts] = React.useState<{ id: number; name: string }[]>([]);
    const [addProductLoading, setAddProductLoading] = React.useState<boolean>(false);
    const [selectedProductId, setSelectedProductId] = React.useState<number | undefined>(undefined);
    const [showDecommissionModal, setShowDecommissionModal] = React.useState<boolean>(false);
    const [decommissionProduct, setDecommissionProduct] = React.useState<NonFeeOfferingDto | undefined>(undefined);
    const [decommissionDate, setDecommissionDate] = React.useState<Dayjs | null>(null);
    const [decommissionError, setDecommissionError] = React.useState<string>();
    const [isDecommissionSaving, setIsDecommissionSaving] = React.useState<boolean>(false);
    const [showRecommissionModal, setShowRecommissionModal] = React.useState<boolean>(false);
    const [recommissionProduct, setRecommissionProduct] = React.useState<NonFeeOfferingDto | undefined>(undefined);
    const [isRecommissionSaving, setIsRecommissionSaving] = React.useState<boolean>(false);
    const [showExcludeModal, setShowExcludeModal] = React.useState<boolean>(false);
    const [excludeProduct, setExcludeProduct] = React.useState<NonFeeOfferingDto | undefined>(undefined);
    const [selectedExcludeSystems, setSelectedExcludeSystems] = React.useState<string[]>([]);
    const [bookingSystems, setBookingSystems] = React.useState<BookingSystem[]>([]);
    const [bookingSystemsLoading, setBookingSystemsLoading] = React.useState<boolean>(false);
    const [bookingSystemsError, setBookingSystemsError] = React.useState<string>();
    const [isExcludeSaving, setIsExcludeSaving] = React.useState<boolean>(false);
    const [excludeSelections, setExcludeSelections] = React.useState<Record<string, string[]>>({});
    const [excludeLoading, setExcludeLoading] = React.useState<boolean>(false);
    const [baselineExcludeSystems, setBaselineExcludeSystems] = React.useState<string[]>([]);
    const [breakdownNameMap, setBreakdownNameMap] = React.useState<Map<string, string>>(new Map());
    const [hoveredProductKey, setHoveredProductKey] = React.useState<string | null>(null);

    const breakdownsModal = useBreakdownsModal({ breakdownNameMap });

    const today = React.useMemo(() => dayjs().startOf("day"), []);

      const selectedPricingYearValue = React.useMemo(
          () =>
              pricingYears.find(
                  (py) => (py as any).id === selectedPricingYear
              )?.year,
          [pricingYears, selectedPricingYear]
      );

    const centreCurrencyIso = React.useMemo(
      () => getCentreCurrencyIso(centres, selectedCentreCode),
      [centres, selectedCentreCode]
    );

    const maxPublishedPricingYear = React.useMemo(() => {
      if (!pricingYears || pricingYears.length === 0)
        return undefined;
      return pricingYears.reduce(
        (max, year) => Math.max(max, (year as any).year),
        (pricingYears[0] as any).year
      );
    }, [pricingYears]);

    const maxDecommissionDate = React.useMemo(
      () =>
        maxPublishedPricingYear
          ? dayjs(`${maxPublishedPricingYear}-12-31`)
          : undefined,
      [maxPublishedPricingYear]
    );

    const centreSchools = React.useMemo(
      () =>
        selectedCentreCode
          ? schools.filter(
              (school) =>
                (school as any).centreCode === selectedCentreCode
            )
          : [],
      [schools, selectedCentreCode]
    );

    const centreSchoolOptions = React.useMemo(
      () =>
        centreSchools
          .map((school) => {
            const id = toSchoolIdNumber((school as any).id);
            return id ? { id, name: school.name } : null;
          })
          .filter(Boolean) as { id: number; name: string }[],
      [centreSchools]
    );

    const enabledCurrenciesRaw = React.useMemo(
      () => getEnabledCurrencies(draftPricelist ?? [], centreCurrencyIso),
      [draftPricelist, centreCurrencyIso]
    );
    const enabledCurrenciesStableRef = React.useRef(enabledCurrenciesRaw);
    const enabledCurrencies = React.useMemo(() => {
      const prev = enabledCurrenciesStableRef.current;
      if (
        prev.length === enabledCurrenciesRaw.length &&
        prev.every((c, i) => c === enabledCurrenciesRaw[i])
      ) {
        return prev;
      }
      enabledCurrenciesStableRef.current = enabledCurrenciesRaw;
      return enabledCurrenciesRaw;
    }, [enabledCurrenciesRaw]);
    const enabledCurrenciesRef = React.useRef(enabledCurrencies);
    enabledCurrenciesRef.current = enabledCurrencies;
    const centreCurrencyIsoRef = React.useRef(centreCurrencyIso);
    centreCurrencyIsoRef.current = centreCurrencyIso;

    React.useImperativeHandle(
      ref,
      () => ({
        beginAdd: () => setAddModalVisible(true),
      }),
      []
    );

    React.useEffect(() => {
      let isMounted = true;

      const loadBookingSystems = async () => {
        try {
          setBookingSystemsLoading(true);
          const systems = await (api as any).getAllBookingSystems();
          if (!isMounted) return;
          setBookingSystems(systems || []);
          setBookingSystemsError(undefined);
        } catch (err: any) {
          if (isMounted)
            setBookingSystemsError(
              err?.message || "Failed to load booking systems."
            );
        } finally {
          if (isMounted) setBookingSystemsLoading(false);
        }
      };

      if (bookingSystems.length === 0 && !bookingSystemsLoading)
        loadBookingSystems();

      return () => {
        isMounted = false;
      };
    }, []);

    React.useEffect(() => {
      if (!addModalVisible) {
        setSelectedProductId(undefined);
        return;
      }

      let cancelled = false;

      const loadProducts = async () => {
        try {
          setAddProductLoading(true);

          const divisionId = getDivisionId();
          let products: any[] = [];

          switch (selectedProductType) {
            case "Course":
              products = divisionId
                ? await (api as any).getCourseProducts(divisionId)
                : [];
              break;
            case "Accommodation":
              if (divisionId) {
                const allAccommodation = await (api as any).getAccommodationProducts() ?? [];
                products = Array.isArray(allAccommodation)
                  ? allAccommodation.filter((a: any) => Number(a.division ?? a.divisionId) === divisionId)
                  : [];
              }
              break;
            case "AddOn":
              products = divisionId
                ? await (api as any).getAddonsProducts(divisionId)
                : [];
              break;
            case "Transfer":
              products = divisionId
                ? await (api as any).getTransferProducts(divisionId)
                : [];
              break;
            case "Package":
              products = divisionId
                ? await (api as any).getPackageProducts(divisionId)
                : [];
              break;
            default:
              console.warn(
                `Unknown product type: ${selectedProductType}`
              );
              products = [];
          }

          if (!cancelled && Array.isArray(products)) {
            const existingOfferingKeys = new Set(
              (draftPricelist ?? [])
                .filter(isNonFeeOffering)
                .filter(
                  (entry) =>
                    entry.productId?.type?.name === selectedProductType
                )
                .map((entry) => buildNonFeeOfferingKey(entry as any))
            );

            const available = products.filter((p) => {
              const isActive =
                p.active !== false &&
                (p.active === true || !p.decommissionDate || !p.closureDate);
              const key = `${selectedProductType}-${Number(p.id)}`;
              return isActive && !existingOfferingKeys.has(key);
            });

            setAvailableProducts(available);
          }
        } catch (error) {
          console.warn("Failed to load products", error);
          setAvailableProducts([]);
        } finally {
          if (!cancelled) setAddProductLoading(false);
        }
      };

      loadProducts();
      return () => {
        cancelled = true;
      };
    }, [
      addModalVisible,
      selectedProductType,
      draftPricelist,
      centreCurrencyIso,
    ]);

    const handleAddProduct = () => {
      if (
        !selectedProductId ||
        !selectedPricingYear ||
        !selectedCentreCode
      )
        return;

      if (!centreSchools.length) {
        message.warning("No schools found for the selected centre.");
        return;
      }

      const schoolIds = centreSchoolOptions.map((s) => ({ id: s.id }));

      const productToAdd = availableProducts.find(
        (p) => p.id === selectedProductId
      );
      if (!productToAdd) return;

      const centre = centres.find(
        (c) => (c as any).code === selectedCentreCode
      );

      const pricingYearRec = pricingYears.find(
        (y) => (y as any).id === selectedPricingYear
      );
      if (!pricingYearRec) {
        message.error("Pricing year not found.");
        return;
      }

      const currencyIsoCodes = enabledCurrencies.length
        ? enabledCurrencies
        : [normIso((centre as any)?.currencyCode || "EUR")].filter(Boolean);

      const pricingYear = (pricingYearRec as any).year;

      const result = createNewNonFeeOffering({
        productTypeName: selectedProductType,
        productId: selectedProductId,
        name: productToAdd.name,
        schoolIds,
        currencyIsoCodes,
        periodStartDate: `${pricingYear}-01-01`,
        periodEndDate: `${pricingYear}-12-31`,
        dateStrategyAssignments: [],
      });

      if (!result.ok) {
        message.error(result.error);
        return;
      }

      const newEntry: EditPricelistResponse = result.offering as any;

      const updatedDraft = [...(draftPricelist ?? []), newEntry];
dispatch(loadDraftPricelist(updatedDraft));
      dispatch(setFormState(SaveState.PendingChanges));

      setAddModalVisible(false);
      setSelectedProductId(undefined);
      message.success(
        `${selectedProductType} "${productToAdd.name}" added successfully.`
      );
    };

    const resolveOfferingIndex = React.useCallback(
      (productId: ProductId) => {
        const list = draftPricelistRef.current;
        return list
          ? list.findIndex(
              (i) =>
                isNonFeeOffering(i) &&
                sameProductId((i as any).productId, productId)
            )
          : -1;
      },
      []
    );

    const baselineByProductIdText = React.useMemo(
      () =>
        new Map(
          (pricelist ?? [])
            .filter(isNonFeeOffering)
            .map((o: any) => [String(o.productIdText), o])
        ),
      [pricelist]
    );

    const getSchoolKey = React.useCallback((input: any): number | null => {
      if (input == null) return null;
      if (typeof input === "object") {
        const c: any = input;
        if ("id" in c) return getSchoolKey(c.id);
        if ("Id" in c) return getSchoolKey(c.Id);
        if ("schoolId" in c) return getSchoolKey(c.schoolId);
        if ("SchoolId" in c) return getSchoolKey(c.SchoolId);
      }
      const n = Number(input);
      return Number.isFinite(n) ? n : null;
    }, []);

    const collectSchoolIds = React.useCallback(
      (
        offering: any,
        statuses: SchoolOfferingStatus[],
        requireDecommissionDate?: boolean
      ) => {
        const list = Array.isArray(offering?.schoolStatuses)
          ? offering.schoolStatuses
          : [];
        return list
          .filter((s: any) => statuses.includes(s?.status))
          .filter((s: any) =>
            requireDecommissionDate ? !!s?.decommissionDate : true
          )
          .map((s: any) => getSchoolKey(s?.schoolId))
          .filter((id: any): id is number => id != null)
          .map((id: number) => ({ id }));
      },
      [getSchoolKey]
    );

    const buildStatusMap = React.useCallback(
      (offering: any): Map<number, SchoolStatusDto> => {
        const map = new Map<number, SchoolStatusDto>();
        (offering?.schoolStatuses ?? []).forEach(
          (item: SchoolStatusDto) => {
            const id = getSchoolKey((item as any)?.schoolId);
            if (id != null) map.set(id, item);
          }
        );
        return map;
      },
      [getSchoolKey]
    );

    const buildBaselineStatus = React.useCallback(
      (offering: any, schoolId: number): SchoolOfferingStatus => {
        const statusMap = buildStatusMap(offering);
        return statusMap.get(schoolId)?.status ?? "Disabled";
      },
      [buildStatusMap]
    );

      const isStrategyInSelectedYear = React.useCallback(
          (ds: any): boolean => {
              const from = dayjs(ds?.fromDate);
              if (!from.isValid()) return false;

              if (!selectedPricingYearValue) return true;

              const to = ds?.toDate ? dayjs(ds.toDate) : null;

              return (
                  from.year() <= selectedPricingYearValue &&
                  (!to || !to.isValid() || to.year() >= selectedPricingYearValue)
              );
          },
          [selectedPricingYearValue]
      );

      const getOfferingStartFrom = React.useCallback(
          (offering?: NonFeeOfferingDto): Dayjs | null => {
              if (!offering) return null;

              const strategies = Array.isArray((offering as any)?.dateStrategyAssignments)
                  ? (offering as any).dateStrategyAssignments
                      .filter((x: any) => x?.isArchived !== true)
                      .filter(isStrategyInSelectedYear)
                  : [];

              const hasOfferingDatesStrategy = strategies.some(
                  (x: any) => Number(x?.strategyTypeId) === 3
              );

              const strategyDates = strategies
                  .map((x: any) => dayjs(x?.fromDate))
                  .filter((d: Dayjs) => d.isValid())
                  .sort((a: Dayjs, b: Dayjs) => a.valueOf() - b.valueOf());

              const pricePeriodDates = (
                  Array.isArray((offering as any)?.pricePeriods)
                      ? (offering as any).pricePeriods
                      : []
              )
                  .map((x: any) => dayjs(x?.startDate))
                  .filter((d: Dayjs) => {
                      if (!d.isValid()) return false;
                      if (!selectedPricingYearValue) return true;
                      return d.year() === selectedPricingYearValue;
                  })
                  .sort((a: Dayjs, b: Dayjs) => a.valueOf() - b.valueOf());

              const strategyStart = strategyDates[0]?.startOf("day") ?? null;
              const pricePeriodStart = pricePeriodDates[0]?.startOf("day") ?? null;

              return hasOfferingDatesStrategy
                  ? pricePeriodStart ?? strategyStart
                  : strategyStart ?? pricePeriodStart;
          },
          [isStrategyInSelectedYear, selectedPricingYearValue]
      );

      const hasOfferingStarted = React.useCallback(
          (offering?: NonFeeOfferingDto): boolean => {
              const startFrom = getOfferingStartFrom(offering);
              return !!startFrom && !today.isBefore(startFrom, "day");
          },
          [getOfferingStartFrom, today]
      );

  const handleSchoolToggle = React.useCallback(
      (offering: NonFeeOfferingDto, schoolId: number, nextChecked: boolean) => {
      if ((offering as any).isNew === true) return;

      const baseline =
          baselineByProductIdText.get(String((offering as any).productIdText)) ??
          offering;

      const status = buildBaselineStatus(baseline, schoolId);

      let op: "create" | "recommission" | "decommission" | null = null;

      if (nextChecked) {
          if (status === "Inactive") op = "recommission";
          else if (status === "Disabled") op = "create";
      } else {
          if (status === "Active") {
                  if (!hasOfferingStarted(baseline as NonFeeOfferingDto)) {
                      message.error("Offering cannot be decommissioned before it starts.");
                      return;
                  }

          op = "decommission";
      }
  }

      props.queueSchoolOp?.({
          op,
          offering,
          schoolId,
      });
      },
      [
          baselineByProductIdText,
          buildBaselineStatus,
          hasOfferingStarted,
          props.queueSchoolOp,
      ]
  );

    const handleChange = React.useCallback((
      productId: ProductId,
      propertyName: string,
      value: any,
      data: any[] | any,
      index?: number,
      periodIndexOrCurrency?: number | string,
      _currencyIsoCode?: string
    ) => {
      const periodIndex =
        typeof periodIndexOrCurrency === "number"
          ? periodIndexOrCurrency
          : undefined;
      const list = draftPricelistRef.current;
      if (!list) return;
      const itemIndex = resolveOfferingIndex(productId);
      if (itemIndex === -1) return;

    if (propertyName === "schoolIds") {
        const original = list[itemIndex] as any;

        if (JSON.stringify(original.schoolIds ?? []) === JSON.stringify(value ?? []))
            return;

        if (formStateRef.current !== SaveState.PendingChanges) {
            dispatch(setFormState(SaveState.PendingChanges));
        }

        const item = { ...original, schoolIds: value };
        dispatch(updateDraftPricelistItem(item, itemIndex));
        return;
    }

      const currentValue =
        Array.isArray(data) && index !== undefined && data[index]
          ? (data[index] as any)[propertyName]
          : data && typeof data === "object"
          ? (data as any)[propertyName]
          : undefined;

      if (isSameValue(propertyName, currentValue, value)) return;

      const nextValue = coerceIntFieldValue(propertyName, value);

      if (formStateRef.current !== SaveState.PendingChanges) {
        dispatch(setFormState(SaveState.PendingChanges));
      }

      const original = list[itemIndex] as
        | StandardOfferingDto
        | PackageOfferingDto;
      let item: any = { ...original };

      if (
        Array.isArray(data) &&
        index !== undefined &&
        index >= 0 &&
        index < data.length
      ) {
        const updatedElement = { ...data[index], ...{ [propertyName]: nextValue } };
        if ( propertyName === "priceBandStrategyId" && "PriceBandStrategyId" in updatedElement) {
          delete (updatedElement as any).PriceBandStrategyId;
        }

        const isPricePeriodField =
          propertyName === "startDate" ||
          propertyName === "endDate" ||
          propertyName === "priceBandStrategyId";

        if (
          isPricePeriodField &&
          item.pricePeriods &&
          Array.isArray(item.pricePeriods) &&
          index < item.pricePeriods.length
        ) {
          item = {
            ...item,
            pricePeriods: [
              ...item.pricePeriods.slice(0, index),
              updatedElement,
              ...item.pricePeriods.slice(index + 1),
            ],
            isUpdated: true,
          };
        } else {
          if (item.pricePeriods && Array.isArray(item.pricePeriods)) {
            let periodIndexToUse: number;

            if (
              periodIndex !== undefined &&
              periodIndex >= 0 &&
              periodIndex < item.pricePeriods.length
            ) {
              periodIndexToUse = periodIndex;
            } else {
              periodIndexToUse = item.pricePeriods.findIndex(
                (period: any) =>
                  period.priceBands &&
                  Array.isArray(period.priceBands) &&
                  period.priceBands.length === data.length
              );
            }

            if (
              periodIndexToUse >= 0 &&
              item.pricePeriods[periodIndexToUse]?.priceBands &&
              index < item.pricePeriods[periodIndexToUse].priceBands.length
            ) {
              const period = { ...item.pricePeriods[periodIndexToUse] };
              const updatedBands = [
                ...period.priceBands.slice(0, index),
                updatedElement,
                ...period.priceBands.slice(index + 1),
              ];

              item = {
                ...item,
                pricePeriods: [
                  ...item.pricePeriods.slice(0, periodIndexToUse),
                  { ...period, priceBands: updatedBands },
                  ...item.pricePeriods.slice(periodIndexToUse + 1),
                ],
                isUpdated: true,
              };
            } else {
              item = { ...item, isUpdated: true };
            }
          } else {
            item = { ...item, isUpdated: true };
          }
        }
      } else if (
        typeof data === "object" &&
        !Array.isArray(data) &&
        index === undefined
      ) {
        const updated = { ...data, ...{ [propertyName]: nextValue } };
        if (
          propertyName === "priceBandStrategyId" &&
          "PriceBandStrategyId" in updated
        ) {
          delete (updated as any).PriceBandStrategyId;
        }
        item = { ...item, ...updated, isUpdated: true };
      } else {
        item = { ...item, isUpdated: true };
      }

      item.isUpdated = (original as any).isNew === true ? false : true;
      dispatch(updateDraftPricelistItem(item, itemIndex));
    }, [dispatch, resolveOfferingIndex]);

    const handlePriceChange = React.useCallback((
      productId: ProductId,
      periodIndex: number,
      bandIndex: number,
      currencyIsoCode: string,
      fieldName: "pricePerMajorUnit" | "pricePerMinorUnit",
      value: any
    ) => {
      const list = draftPricelistRef.current;
      if (!list) return;
      const itemIndex = resolveOfferingIndex(productId);
      if (itemIndex === -1) return;

      const original = list[itemIndex] as
        | StandardOfferingDto
        | PackageOfferingDto;

      const periods = Array.isArray(original.pricePeriods)
        ? [...original.pricePeriods]
        : [];
      if (!periods[periodIndex]) return;

      const period: any = { ...periods[periodIndex] };
      const bands = Array.isArray(period.priceBands)
        ? [...period.priceBands]
        : [];
      if (!bands[bandIndex]) return;

      const band: any = { ...bands[bandIndex] };
      const prices = Array.isArray(band.prices) ? [...band.prices] : [];
      const iso = normIso(currencyIsoCode);
      if (!iso) return;

      let priceIndex = prices.findIndex(
        (p: any) => normIso(p?.currencyIsoCode) === iso
      );
      if (priceIndex === -1) {
        prices.push({
          currencyIsoCode: iso,
          pricePerMajorUnit: 0,
          pricePerMinorUnit: 0,
        });
        priceIndex = prices.length - 1;
      }

      prices[priceIndex] = {
        ...prices[priceIndex],
        [fieldName]: value,
      };

      bands[bandIndex] = { ...band, prices };
      period.priceBands = bands;
      periods[periodIndex] = period;

      if (formStateRef.current !== SaveState.PendingChanges) {
        dispatch(setFormState(SaveState.PendingChanges));
      }

      const item: any = {
        ...original,
        pricePeriods: periods,
        isUpdated: (original as any).isNew === true ? false : true,
      };

      dispatch(updateDraftPricelistItem(item, itemIndex));
    }, [dispatch, resolveOfferingIndex]);

    const refreshSeqRef = React.useRef(0);
    const handleRefresh = () => {
      if (props.reloadPricelist) {
        props.reloadPricelist();
        return;
      }

      if (
        !selectedCentreCode ||
        !selectedPricingYear ||
        !selectedProductType
      )
        return;

      const schoolIds = centreSchools
        .map((s) => toSchoolIdNumber((s as any).id))
        .filter((id): id is number => Number.isFinite(id))
        .map((id) => ({ id }));
      if (!schoolIds.length) return;

      const seq = ++refreshSeqRef.current;
dispatch(setIsLoading(true));

      const payload = {
        schoolIds,
        pricingYearId: selectedPricingYear,
        type: selectedProductType as OfferingType,
      };

      Promise.resolve(
        (api as any).GetAllActiveOfferings(payload, getDivisionId())
      )
        .then((res: any) => {
          if (seq !== refreshSeqRef.current) return;

          const data = Array.isArray(res)
            ? res
            : res?.ok
            ? res.data ?? []
            : null;
          if (data) dispatch(loadPricelist(data));
          else
            dispatch(
              setFormState(
              SaveState.LoadError,
              res?.errorMessage ?? res?.message ?? "Failed to load pricelist."
            ));
        })
        .finally(() => {
          if (seq !== refreshSeqRef.current) return;
          dispatch(setIsLoading(false));
        });
    };

    const handleClose = () => {
      setShowModal(false);
      if (pendingChanges === true) handleRefresh();
    };

    const handleDraftDateStrategiesSaved = React.useCallback(
      (assignments: DateStrategyAssign[]) => {
        if (!selectedProduct) return;
        const pidText = String((selectedProduct as any).productIdText ?? "");
        const idx = (draftPricelist ?? []).findIndex(
          (p) => String((p as any).productIdText ?? "") === pidText
        );
        if (idx < 0) return;
        const updated = {
          ...selectedProduct,
          dateStrategyAssignments: assignments,
          isUpdated: (selectedProduct as any).isNew ? false : true,
        } as NonFeeOfferingDto;
        dispatch(updateDraftPricelistItem(updated, idx));
        if (formState !== SaveState.PendingChanges) {
          dispatch(setFormState(SaveState.PendingChanges));
        }
      },
      [
        selectedProduct,
        draftPricelist,
        dispatch,
        formState,
      ]
    );

  const openDecommissionModal = React.useCallback((product: NonFeeOfferingDto) => {
      const startFrom = getOfferingStartFrom(product);

      if (!startFrom) {
          message.error("Offering has no start date and cannot be decommissioned.");
          return;
      }

      if (today.isBefore(startFrom, "day")) {
          message.error("Offering cannot be decommissioned before it starts.");
          return;
      }

      setDecommissionProduct(product);
      setDecommissionDate(null);
      setDecommissionError(undefined);
      setShowDecommissionModal(true);
  }, [getOfferingStartFrom, today]);

    const resetDecommissionModal = () => {
      setShowDecommissionModal(false);
      setDecommissionProduct(undefined);
      setDecommissionDate(null);
      setDecommissionError(undefined);
      setIsDecommissionSaving(false);
    };

    const openRecommissionModal = React.useCallback((product: NonFeeOfferingDto) => {
      setRecommissionProduct(product);
      setShowRecommissionModal(true);
    }, []);

    const resetRecommissionModal = () => {
      setShowRecommissionModal(false);
      setRecommissionProduct(undefined);
      setIsRecommissionSaving(false);
    };

    const resolveProductKey = React.useCallback(
      (product?: NonFeeOfferingDto) => {
        if (!product) return "";
        if (
          (product as any).productIdText &&
          String((product as any).productIdText).trim().length > 0
        ) {
          return (product as any).productIdText;
        }
        const productTypeName =
          (product as any).productId?.type?.name ??
          selectedProductType ??
          "product";
        return `${productTypeName}-${
          (product as any).productId?.id ?? "unknown"
        }`;
      },
      [selectedProductType]
    );

    const openExcludeModal = React.useCallback((product: NonFeeOfferingDto) => {
      setExcludeProduct(product);

      const key = resolveProductKey(product);
      setSelectedExcludeSystems(excludeSelections[key] ?? []);

      setBookingSystemsError(undefined);
      setShowExcludeModal(true);
    }, [resolveProductKey, excludeSelections]);

    const resetExcludeModal = () => {
      setShowExcludeModal(false);
      setExcludeProduct(undefined);
      setSelectedExcludeSystems([]);
      setBaselineExcludeSystems([]);
      setIsExcludeSaving(false);
      setExcludeLoading(false);
      setBookingSystemsError(undefined);
    };

    React.useEffect(() => {
      if (!showExcludeModal || !excludeProduct) return;

      const sid = props.selectedSchoolId;
      if (!sid) {
        setBookingSystemsError("Select a school first.");
        setSelectedExcludeSystems([]);
        setBaselineExcludeSystems([]);
        return;
      }

      const productId = (excludeProduct as any).productId?.id ?? 0;
      const productTypeId = (excludeProduct as any).productId?.type?.id ?? 0;

      if (!productId || !productTypeId) {
        setBookingSystemsError("Invalid product id/type.");
        setSelectedExcludeSystems([]);
        setBaselineExcludeSystems([]);
        return;
      }

      const key = resolveProductKey(excludeProduct);

      let cancelled = false;
      setExcludeLoading(true);
      setBookingSystemsError(undefined);

      (async () => {
        try {
          const res = await (api as any).getProductOfferingExclusions(
            sid,
            productTypeId,
            productId
          );
          if (cancelled) return;

          const ids = Array.isArray(res?.excludedBookingSystemIds)
            ? res.excludedBookingSystemIds
            : [];
          const asStrings = ids.map((x: any) => String(x));

          setSelectedExcludeSystems(asStrings);
          setBaselineExcludeSystems(asStrings);

          setExcludeSelections((prev) => ({ ...prev, [key]: asStrings }));
        } catch (err: any) {
          if (cancelled) return;
          setBookingSystemsError(err?.message || "Failed to load exclusions.");
          setSelectedExcludeSystems([]);
          setBaselineExcludeSystems([]);
        } finally {
          if (!cancelled) setExcludeLoading(false);
        }
      })();

      return () => {
        cancelled = true;
      };
    }, [
      showExcludeModal,
      excludeProduct,
      props.selectedSchoolId,
      resolveProductKey,
    ]);

  const disableDecommissionDate = (current?: Dayjs) => {
      if (!current) return false;

      const normalized = current.startOf("day");
      const startFrom = getOfferingStartFrom(decommissionProduct);

      if (!normalized.isAfter(today, "day")) return true;
      if (startFrom && normalized.isBefore(startFrom, "day")) return true;
      if (maxDecommissionDate && !normalized.isBefore(maxDecommissionDate, "day"))
          return true;

      return false;
  };

  const validateDecommissionSelection = (value: Dayjs | null) => {
      if (!value) return "Select a decommission date.";
      if (!decommissionProduct) return "Unable to find the selected product.";

      const startFrom = getOfferingStartFrom(decommissionProduct);
      if (!startFrom) {
          return "Offering has no start date and cannot be decommissioned.";
      }

      if (today.isBefore(startFrom, "day")) {
          return "Offering cannot be decommissioned before it starts.";
      }

      const limitYear = maxDecommissionDate
      ? maxDecommissionDate.year()
      : today.year();

      const invalidMessage = `Decommission date must be in the future and before 31-Dec-${limitYear}`;
      const normalized = value.startOf("day");

      if (!normalized.isAfter(today, "day")) return invalidMessage;
      if (normalized.isBefore(startFrom, "day")) {
          return `Decommission date must be on or after offering start date (${startFrom.format(
              "YYYY-MM-DD"
          )}).`;
      }
      if (maxDecommissionDate && !normalized.isBefore(maxDecommissionDate, "day")) {
          return invalidMessage;
      }

      return undefined;
  };

    const executeDecommission = async (
      payload: DecommissionProductOffering,
      divisionId: number
    ) => {
      try {
        setIsDecommissionSaving(true);
        const result = await (api as any).Decommission(payload, divisionId);
        const responseMessage =
          result && typeof result.message === "string" ? result.message : "";
        const normalizedMessage = responseMessage.trim().toLowerCase();

        const treatedAsSuccess =
          !!result?.ok ||
          normalizedMessage === "ok" ||
          normalizedMessage.includes("success");

        if (treatedAsSuccess) {
          message.success(
            responseMessage && normalizedMessage !== "ok"
              ? responseMessage
              : `Product '${
                  decommissionProduct?.name || ""
                }' was scheduled for decommission.`
          );
          resetDecommissionModal();
          handleRefresh();
        } else {
          setIsDecommissionSaving(false);
          message.error(
            responseMessage?.trim()?.length
              ? responseMessage
              : "Failed to save decommission date."
          );
        }
      } catch (err: any) {
        setIsDecommissionSaving(false);
        message.error(err?.message || "Failed to save decommission date.");
      }
    };

    const handleDecommissionConfirm = () => {
      if (!decommissionProduct) {
        message.error("Unable to find the selected product.");
        return;
      }

      const err = validateDecommissionSelection(decommissionDate);
      if (err) {
        setDecommissionError(err);
        return;
      }
      if (!decommissionDate) {
        setDecommissionError("Select a decommission date.");
        return;
      }

      const normalizedDate = decommissionDate.startOf("day");
      const decommissionDateString = normalizedDate.format("YYYY-MM-DD");
      const divisionId = getDivisionId();

      const pricingYearValue =
        pricingYears.find(
          (py) => (py as any).id === selectedPricingYear
        )?.year ??
        maxPublishedPricingYear ??
        today.year();

      const computedProductIdText =
        (decommissionProduct as any).productIdText ??
        ((decommissionProduct as any).productId?.type?.name &&
        (decommissionProduct as any).productId?.id !== undefined
          ? `${(decommissionProduct as any).productId.type.name}-${
              (decommissionProduct as any).productId.id
            }`
          : selectedProductType
          ? `${selectedProductType}-${
              (decommissionProduct as any).productId?.id ?? "unknown"
            }`
          : `product-${
              (decommissionProduct as any).productId?.id ?? "unknown"
            }`);

    const schoolIds = collectSchoolIds(decommissionProduct, ["Active"]);
    if (!schoolIds.length) {
        message.error("Select at least one school.");
        return;
    }

      const payload: DecommissionProductOffering = {
        schoolIds,
        productIdText: computedProductIdText,
        pricingYear: pricingYearValue,
        decommissionDate: decommissionDateString,
        type: ((decommissionProduct as any).productId?.type?.name ??
          selectedProductType) as OfferingType,
      };

      Modal.confirm({
        title: "Confirm Decommission",
        content: `Are you sure you want to decommission the following product '${
          (decommissionProduct as any).name
        }'?`,
        okText: "Yes, Decommission",
        cancelText: "Cancel",
        onOk: () => executeDecommission(payload, divisionId),
      });
    };

    const executeRecommission = async (
      payload: RecommissionProductOffering,
      divisionId: number
    ) => {
      try {
        setIsRecommissionSaving(true);

        const result = await (api as any).Recommission(payload, divisionId);
        const responseMessage =
          result && typeof result.message === "string" ? result.message : "";
        const normalizedMessage = responseMessage.trim().toLowerCase();

        const treatedAsSuccess =
          !!result?.ok ||
          normalizedMessage === "ok" ||
          normalizedMessage.includes("success");

        if (treatedAsSuccess) {
          message.success(
            responseMessage && normalizedMessage !== "ok"
              ? responseMessage
              : `Product '${
                  recommissionProduct?.name || ""
                }' was recommissioned.`
          );
          resetRecommissionModal();
          handleRefresh();
        } else {
          setIsRecommissionSaving(false);
          message.error(
            responseMessage?.trim()?.length
              ? responseMessage
              : "Failed to recommission product offering."
          );
        }
      } catch (err: any) {
        setIsRecommissionSaving(false);
        message.error(
          err?.message || "Failed to recommission product offering."
        );
      }
    };

    const handleRecommissionConfirm = () => {
      if (!recommissionProduct) {
        message.error("Unable to find the selected product.");
        return;
      }

      const divisionId = getDivisionId();

    const schoolIds = collectSchoolIds(
        recommissionProduct,
        ["Inactive"],
        true
    );
    if (!schoolIds.length) {
        message.error("Select at least one school.");
        return;
    }

      const pricingYearValue =
        pricingYears.find(
          (py) => (py as any).id === selectedPricingYear
        )?.year ??
        maxPublishedPricingYear ??
        today.year();

      const computedProductIdText =
        (recommissionProduct as any).productIdText ??
        ((recommissionProduct as any).productId?.type?.name &&
        (recommissionProduct as any).productId?.id !== undefined
          ? `${(recommissionProduct as any).productId.type.name}-${
              (recommissionProduct as any).productId.id
            }`
          : selectedProductType
          ? `${selectedProductType}-${
              (recommissionProduct as any).productId?.id ?? "unknown"
            }`
          : `product-${
              (recommissionProduct as any).productId?.id ?? "unknown"
            }`);

      const payload: RecommissionProductOffering = {
        schoolIds,
        productIdText: computedProductIdText,
        pricingYear: pricingYearValue,
        type: ((recommissionProduct as any).productId?.type?.name ??
          selectedProductType) as OfferingType,
      };

      Modal.confirm({
        title: "Confirm Recommission",
        content: `Are you sure you want to recommission the following product '${
          (recommissionProduct as any).name
        }'?`,
        okText: "Yes, Recommission",
        cancelText: "Cancel",
        onOk: () => executeRecommission(payload, divisionId),
      });
    };

    const handleExcludeConfirm = async () => {
      if (!excludeProduct) {
        message.error("Unable to find the selected product.");
        return;
      }

      if (
        excludeBookingSystemIdsEqual(
          selectedExcludeSystems,
          baselineExcludeSystems
        )
      ) {
        return;
      }

      const sid = props.selectedSchoolId;
      if (!sid) {
        message.error("Select a school first.");
        return;
      }

      const productId = (excludeProduct as any).productId?.id ?? 0;
      const productTypeId = (excludeProduct as any).productId?.type?.id ?? 0;

      if (!productId || !productTypeId) {
        message.error("Invalid product id/type.");
        return;
      }

      const bookingSystemIds = selectedExcludeSystems
        .map((x) => Number(x))
        .filter((x) => Number.isFinite(x));

      try {
        setIsExcludeSaving(true);

        const result = await (api as any).setProductOfferingExclusions(
          sid,
          productTypeId,
          productId,
          bookingSystemIds
        );

        if (result?.ok === false) {
          setIsExcludeSaving(false);
          message.error(
            result?.message?.trim()?.length
              ? result.message
              : "Failed to update booking system exclusions."
          );
          return;
        }

        const key = resolveProductKey(excludeProduct);
        setExcludeSelections((prev) => ({
          ...prev,
          [key]: [...selectedExcludeSystems],
        }));

        message.success(
          `Booking system exclusions updated for '${
            (excludeProduct as any).name
          }'.`
        );
        resetExcludeModal();
      } catch (err: any) {
        setIsExcludeSaving(false);
        message.error(
          err?.message || "Failed to update booking system exclusions."
        );
      }
    };

    const [validationDraft, setValidationDraft] = React.useState(draftPricelist);
    React.useEffect(() => {
      if (!editMode) {
        setValidationDraft(draftPricelist);
        return;
      }
      const timer = window.setTimeout(() => {
        setValidationDraft(draftPricelist);
      }, 300);
      return () => window.clearTimeout(timer);
    }, [draftPricelist, editMode]);

    const weekBracketErrors = React.useMemo((): FormError[] => {
      if (!editMode) return [];

      const bandErrors: FormError[] = [];
      const offerings = (validationDraft ?? [])
        .filter(isNonFeeOffering)
        .filter((p) => {
          const typeName = (p as any)?.productId?.type?.name ?? (p as any)?.type;
          return (
            typeName === selectedProductType &&
            typeof (p as any)?.productId?.id === "number" &&
            Array.isArray((p as any).pricePeriods) &&
            (p as any).pricePeriods.length > 0
          );
        });

      offerings.forEach((offering, pi) => {
        const periods = (offering as any).pricePeriods ?? [];
        periods.forEach((period: PricePeriods, ppi: number) => {
          validatePriceBandsForPeriod(period.priceBands, pi, ppi).forEach(
            ({ name, error }) => {
              bandErrors.push({
                name,
                error,
                message: error,
                field: undefined,
              });
            }
          );
        });
      });

      return bandErrors;
    }, [editMode, validationDraft, selectedProductType]);

    const mergeFormErrors = React.useCallback(
      (fieldErrors: FormError[]) => [
        ...fieldErrors.filter((e) => !isPriceBandFieldErrorName(e.name)),
        ...weekBracketErrors,
      ],
      [weekBracketErrors]
    );
    const mergeFormErrorsRef = React.useRef(mergeFormErrors);
    mergeFormErrorsRef.current = mergeFormErrors;

    React.useEffect(() => {
      const next = mergeFormErrors(formErrorsRef.current ?? []);
      const current = formErrorsRef.current ?? [];
      const changed =
        next.length !== current.length ||
        next.some(
          (e, i) => e.name !== current[i]?.name || e.error !== current[i]?.error
        );
      if (changed) {
        dispatch(setFormErrors(next));
      }
    }, [mergeFormErrors, dispatch]);

    const handleError = React.useCallback((errors: FormError[]) => {
      const next = mergeFormErrorsRef.current(errors);
      const current = formErrorsRef.current ?? [];
      const changed =
        next.length !== current.length ||
        next.some(
          (e, i) => e.name !== current[i]?.name || e.error !== current[i]?.error
        );
      if (changed) {
        dispatch(setFormErrors(next));
      }
    }, [dispatch]);

    const handleContextMenuAction = React.useCallback((
      action: string,
      productId: ProductId,
      isPricePeriod: boolean,
      pricePeriodIndex: number,
      priceBandIndex: number
    ) => {
      const list = draftPricelistRef.current;
      if (!list) return;
      const itemIndex = resolveOfferingIndex(productId);
      if (itemIndex === -1) return;

      const item = { ...(list[itemIndex] as any) };
      const currencies = enabledCurrenciesRef.current;
      const centreIso = centreCurrencyIsoRef.current;

      if (action === "AddRowAbove" || action === "AddRowBelow") {
        const targetCurrencies =
          currencies.length > 0
            ? currencies
            : [centreIso].filter(Boolean);
        const defaultPriceBand: any = {
          minUnits: 1,
          maxUnits: 2147483647,
          prices: targetCurrencies.map((iso) => ({
            currencyIsoCode: iso,
            pricePerMajorUnit: 0,
            pricePerMinorUnit: 0,
          })),
          isNew: true,
        };

        if (isPricePeriod) {
          const dateOffset: number =
            pricePeriodIndex === 0 ||
            pricePeriodIndex === item.pricePeriods.length - 1
              ? 7
              : 0;
          const pivot = new Date(
            action === "AddRowAbove"
              ? item.pricePeriods[pricePeriodIndex].startDate
              : item.pricePeriods[pricePeriodIndex].endDate
          );

          const startDate = new Date(pivot);
          const endDate = new Date(pivot);

          if (action === "AddRowAbove") {
            startDate.setDate(pivot.getDate() - dateOffset);
            endDate.setDate(pivot.getDate());
          } else {
            startDate.setDate(pivot.getDate());
            endDate.setDate(pivot.getDate() + dateOffset);
          }

          const pricePeriodStart =
            action === "AddRowAbove" ? pricePeriodIndex : pricePeriodIndex + 1;

          const defaultPricePeriod: any = {
            startDate: dayjs(startDate).format("YYYY-MM-DD"),
            endDate: dayjs(endDate).format("YYYY-MM-DD"),
            priceBandStrategyId: 1,
            priceBands: [defaultPriceBand],
            isNew: true,
          };

          const periods = Array.isArray(item.pricePeriods)
            ? [...item.pricePeriods]
            : [];
          periods.splice(pricePeriodStart, 0, defaultPricePeriod);
          item.pricePeriods = periods;
        } else {
          const periods = Array.isArray(item.pricePeriods) ? [...item.pricePeriods] : [];
          const period = { ...(periods[pricePeriodIndex] ?? {}) } as any;

          const bands = Array.isArray(period.priceBands) ? [...period.priceBands] : [];
          const insertIndex =
              action === "AddRowAbove" ? priceBandIndex : priceBandIndex + 1;

          period.priceBands = insertPriceBand(bands, insertIndex, targetCurrencies);

          periods[pricePeriodIndex] = period;
          item.pricePeriods = periods;
        }

      } else if (action === "DeleteRow") {
        if (isPricePeriod) {
          const periods = Array.isArray(item.pricePeriods)
            ? [...item.pricePeriods]
            : [];
          periods.splice(pricePeriodIndex, 1);
          item.pricePeriods = periods;
        } else {
          const periods = Array.isArray(item.pricePeriods) ? [...item.pricePeriods] : [];
          const period = { ...(periods[pricePeriodIndex] ?? {}) } as any;
          const bands = Array.isArray(period.priceBands) ? [...period.priceBands] : [];

          bands.splice(priceBandIndex, 1);
          period.priceBands = normalizePriceBands(bands);

          periods[pricePeriodIndex] = period;
          item.pricePeriods = periods;
        }

      }

      item.isUpdated = (item as any).isNew === true ? false : true;
      dispatch(updateDraftPricelistItem(item, itemIndex));
      dispatch(setFormState(SaveState.PendingChanges));
    }, [dispatch, resolveOfferingIndex]);

    const generateContextMenu = React.useCallback((
      hasPendingChanges: boolean,
      product: NonFeeOfferingDto,
      period: PricePeriods,
      band: PriceBands,
      pi: number,
      ppi: number,
      pbi: number,
      isPricePeriod: boolean = true
    ) => {
      const allowDelete: boolean = isPricePeriod
        ? (product as any).pricePeriods.length > 1
        : (period as any).priceBands.length > 1;

      return (
        <ContextMenuTrigger
          hasPendingChanges={hasPendingChanges}
          allowDelete={allowDelete}
          offeringKey={buildNonFeeOfferingKey(product as any)}
          productId={(product as any).productId}
          isPricePeriod={isPricePeriod}
          ppi={ppi}
          pbi={pbi}
          onAction={handleContextMenuAction}
        />
      );
    }, [handleContextMenuAction]);

    const loadedBreakdownTypeIds = React.useRef<Set<number>>(new Set());

    React.useEffect(() => {
      if (selectedProductType !== "Package") {
        setBreakdownNameMap(new Map());
        loadedBreakdownTypeIds.current = new Set();
        return;
      }

      const packages = (draftPricelist ?? []).filter(isPackageOffering);

      const neededTypeIds = new Set<number>();
      packages.forEach((p) => {
        (p.packageOfferingBreakdowns ?? []).forEach((b) => {
          const tid = Number((b as any)?.productTypeId);
          if (Number.isFinite(tid) && tid > 0) neededTypeIds.add(tid);
        });
      });

      const missing = [...neededTypeIds].filter(
        (tid) => !loadedBreakdownTypeIds.current.has(tid)
      );
      if (missing.length === 0) return;

      let cancelled = false;

      (async () => {
        const divisionId = getDivisionId();

        const fetchByTypeId = async (typeId: number): Promise<any[]> => {
          switch (typeId) {
            case 1:
              return divisionId
                ? await (api as any).getCourseProducts(divisionId)
                : [];
            case 2: {
              return divisionId
                ? await (api as any).getAccommodationRoomProductsByDivision(divisionId)
                : [];
            }
            case 3:
              return divisionId
                ? await (api as any).getAddonsProducts(divisionId)
                : [];
            case 4:
              return divisionId
                ? await (api as any).getTransferProducts(divisionId)
                : [];
            case 5:
              return divisionId
                ? await (api as any).getPackageProducts(divisionId)
                : [];
            case 6:
              return divisionId
                ? await api.getAllFeesForDivision(divisionId)
                : [];
            default:
              return [];
          }
        };

        const results = await Promise.all(
          missing.map(async (typeId) => ({
            typeId,
            items: await fetchByTypeId(typeId),
          }))
        );

        if (cancelled) return;

        setBreakdownNameMap((prev) => {
          const next = new Map(prev);
          results.forEach((r) => {
            (r.items ?? []).forEach((x: any) => {
              const pid = Number(x?.id);
              const name = String(x?.name ?? x?.productName ?? "").trim();
              if (Number.isFinite(pid) && pid > 0 && name) {
                next.set(`${r.typeId}-${pid}`, name);
              }
            });
          });
          return next;
        });

        missing.forEach((tid) => loadedBreakdownTypeIds.current.add(tid));
      })();

      return () => {
        cancelled = true;
      };
    }, [selectedProductType, draftPricelist]);

    const columns = React.useMemo(
      () =>
        createProductTableColumns({
          selectedProductType: selectedProductType,
          pricingYears: pricingYears,
          selectedPricingYear: selectedPricingYear,
          standaloneFields: true,
          enabledCurrencies,
          centreSchools: centreSchoolOptions,
          breakdownNameMap,
          handleChange,
          handlePriceChange,
          onSchoolToggle: handleSchoolToggle,
          handleError,
          generateContextMenu,
          openBreakdownsModal: breakdownsModal.open,
          openDecommissionModal,
          openRecommissionModal,
          openExcludeModal,
          setSelectedProduct,
          setDateStrategies,
          setShowModal,
          setPendingChanges,
          BreakdownLegendLine,
          rowHighlightColors: props.rowHighlightColors,
          onRowHighlightChange: props.onRowHighlightChange,
          allowedStartDate: props.allowedStartDate,
          allowedEndDate: props.allowedEndDate,
        }),
      [
        selectedProductType,
        pricingYears,
        selectedPricingYear,
        enabledCurrencies,
        centreSchoolOptions,
        breakdownNameMap,
        handleChange,
        handlePriceChange,
        handleSchoolToggle,
        handleError,
        generateContextMenu,
        breakdownsModal.open,
        openDecommissionModal,
        openRecommissionModal,
        openExcludeModal,
        setSelectedProduct,
        setDateStrategies,
        setShowModal,
        setPendingChanges,
        props.rowHighlightColors,
        props.onRowHighlightChange,
        props.allowedStartDate,
        props.allowedEndDate,
      ]
    );

    const filteredOfferings = React.useMemo(() => {
      const matchesStatus = (p: any) => {
        const statuses = Array.isArray(p?.schoolStatuses)
          ? p.schoolStatuses
          : [];
        const hasActiveOrScheduled = statuses.some(
          (s: any) =>
            s?.status === "Active" || s?.status === "ScheduledInactive"
        );
        const fallbackActive =
          !statuses.length && Array.isArray(p?.schoolIds) && p.schoolIds.length > 0;
        const isActive = hasActiveOrScheduled || fallbackActive;
        if (props.showInactiveOnly) return true;
        return isActive;
      };

      return (draftPricelist || [])
        .filter(isNonFeeOffering)
        .filter((p) => {
          const typeName = p?.productId?.type?.name ?? p?.type;
          return (
            typeName === selectedProductType &&
            typeof p?.productId?.id === "number"
          );
        })
        .filter(matchesStatus);
    }, [draftPricelist, selectedProductType, props.showInactiveOnly]);

    const flatRowsCacheRef = React.useRef<Map<string, FlatRow>>(new Map());
    const { flatRows } = React.useMemo(() => {
      const built = buildProductFlatRows(filteredOfferings, selectedProductType);
      const prevCache = flatRowsCacheRef.current;
      const nextCache = new Map<string, FlatRow>();
      const stableRows = built.flatRows.map((row) => {
        const prev = prevCache.get(row.key);
        if (
          prev &&
          prev.product === row.product &&
          prev.period === row.period &&
          prev.band === row.band &&
          prev.pi === row.pi &&
          prev.ppi === row.ppi &&
          prev.pbi === row.pbi &&
          prev.productRowSpan === row.productRowSpan &&
          prev.periodRowSpan === row.periodRowSpan
        ) {
          nextCache.set(row.key, prev);
          return prev;
        }
        nextCache.set(row.key, row);
        return row;
      });
      flatRowsCacheRef.current = nextCache;
      return { flatRows: stableRows };
    }, [filteredOfferings, selectedProductType]);

    const getProductHoverKey = React.useCallback(
      (row: FlatRow) =>
        buildNonFeeOfferingKey(row.product as any),
      [centreCurrencyIso]
    );

    const productGroupOrderMap = React.useMemo(() => {
      const map = new Map<string, number>();
      let order = 0;
      flatRows.forEach(row => {
        const id = row.product.productId.id.toString();
        if (!map.has(id)) {
          map.set(id, order++);
        }
      });
      return map;
    }, [flatRows]);

    const tableRowClassName = React.useCallback(
      (row: FlatRow) => {
        const baseClass = getProductRowClassName(row);
        const groupOrder = productGroupOrderMap.get(row.product.productId.id.toString()) ?? 0;
        const isOdd = groupOrder % 2 === 1;

        const isHovered = hoveredProductKey === getProductHoverKey(row);
        const offeringKey = buildNonFeeOfferingKey(row.product as any);
        const hasHighlight = Boolean(props.rowHighlightColors?.[offeringKey]);
        const isEditingOffering =
          editMode && editingOfferingKey === offeringKey;

        const classes = [
          baseClass,
          isHovered ? "hovered-row" : undefined,
          isOdd ? "odd-row" : "even-row",
          hasHighlight ? "pp-row-highlighted" : undefined,
          isEditingOffering ? "pp-row-editing" : undefined,
          editMode ? "pp-row-edit-mode" : undefined,
        ];

        return classes.filter(Boolean).join(" ");
      },
      [
        hoveredProductKey,
        getProductHoverKey,
        productGroupOrderMap,
        props.rowHighlightColors,
        editMode,
        editingOfferingKey,
      ]
    );

    const tableRowEvents = React.useCallback(
      (row: FlatRow) => {
        const groupKey = getProductHoverKey(row);
        const offeringKey = buildNonFeeOfferingKey(row.product as any);
        const bgColor = props.rowHighlightColors?.[offeringKey];
        return {
          onMouseEnter: () => setHoveredProductKey(groupKey),
          onMouseLeave: () =>
            setHoveredProductKey((current) =>
              current === groupKey ? null : current
            ),
          onClick: () => {
            if (!editMode) return;
            if ((row.product as any).hasPendingChanges === true) return;
            setEditingOfferingKey(offeringKey);
          },
          ...(bgColor
            ? ({ style: { "--pp-row-highlight": bgColor } as React.CSSProperties })
            : {}),
        };
      },
      [getProductHoverKey, props.rowHighlightColors, editMode]
    );

    function renderTable() {
      if (
        !isLoading &&
        !draftPricelist &&
        errorMessage &&
        errorMessage !== ""
      ) {
        const isDraftLoadError =
          formState === SaveState.LoadError &&
          typeof errorMessage === "string" &&
          /ticks|draft|unpublished/i.test(errorMessage);

        return (
          <Alert
            title={`Error while ${
              formState === SaveState.LoadError ? "loading" : "saving"
            } data!`}
            description={
              isDraftLoadError ? (
                <>
                  {errorMessage}{" "}
                  <Link to="/pricelist/draft">Open the Draft Pricing Year</Link> to view
                  unpublished changes.
                </>
              ) : (
                <div style={{ whiteSpace: "pre-line" }}>{errorMessage}</div>
              )
            }
            type="error"
            showIcon
          />
        );
      }

      if (!isLoading && draftPricelist) {
        const filteredProductOfferings = filteredOfferings;

        const hasPendingChanges: boolean =
          filteredProductOfferings &&
          filteredProductOfferings.find(
            (p) => (p as any).hasPendingChanges === true
          ) === undefined
            ? false
            : true;

        return (
          <>
            {/* {hasPendingChanges && (
              <Alert
                title="Unpublished Changes"
                description={
                  <>
                    There are some unpublished changes. Affected products are
                    locked and cannot be edited. Please wait for changes to be
                    published first.{" "}
                    <a className="pointer" onClick={() => handleRefresh()}>
                      Refresh Page
                    </a>
                  </>
                }
                type="warning"
                showIcon
                style={{ marginBottom: 16 }}
              />
            )} */}

            {!isLoading &&
            filteredProductOfferings &&
            filteredProductOfferings.length === 0 ? (
              <Alert
                title={`No ${selectedProductType.toLowerCase()} products found`}
                type="info"
                showIcon
                style={{ marginTop: 16 }}
              />
            ) : null}

            {errorMessage && errorMessage !== "" && (
              <Alert
                title={`Error while ${
                  formState === SaveState.LoadError ? "loading" : "saving"
                } data!`}
                description={
                  <div style={{ whiteSpace: "pre-line" }}>{errorMessage}</div>
                }
                type="error"
                showIcon
              />
            )}

            <Modal
              centered
              open={!!showModal}
              width={960}
              styles={{ body: { padding: "20px 24px 24px" } }}
              title={`Date Strategies${
                selectedProduct ? ` for '${(selectedProduct as any).name}'` : ""
              }`}
              onCancel={handleClose}
              destroyOnHidden
              footer={[
                <Button type="primary" key="close" onClick={handleClose}>
                  Close
                </Button>,
              ]}
            >
              <DateStrategyList
                key={`${(selectedProduct as any)?.productIdText ?? ""}-${selectedPricingYear ?? ""}`}
                schoolStatuses={
                  Array.isArray((selectedProduct as any)?.schoolStatuses)
                    ? (selectedProduct as any).schoolStatuses
                    : []
                }
                schoolIds={
                  Array.isArray((selectedProduct as any)?.schoolIds)
                    ? (selectedProduct as any).schoolIds
                    : []
                }
                selectedProduct={selectedProduct}
                selectedProductType={selectedProductType}
                dateStrategies={dateStrategies}
                originalDateStrategies={JSON.parse(
                  JSON.stringify(dateStrategies)
                )}
                onDateStrategiesChanged={(assignments) => {
                  const nextAssignments = assignments ?? [];
                  setDateStrategies(nextAssignments);

                  if (selectedProduct) {
                    const nextSelected = {
                      ...(selectedProduct as any),
                      dateStrategyAssignments: nextAssignments,
                    } as any;
                    setSelectedProduct(nextSelected);

                    const pidText = String((nextSelected as any)?.productIdText ?? "");
                    const idx = (draftPricelist ?? []).findIndex(
                      (p: any) => String(p?.productIdText ?? "") === pidText
                    );
                    if (idx >= 0) {
                      dispatch(updateDraftPricelistItem(nextSelected, idx));
                    }
                  }
                }}
                onPendingChanges={(changes) => setPendingChanges(changes)}
                persistDraftOnly={props.isDraftPricelist === true}
                onDraftDateStrategiesSaved={handleDraftDateStrategiesSaved}
                onDateStrategyPersisted={
                  props.isDraftPricelist === true ? undefined : handleRefresh
                }
                allowEditNewStrategy={(selectedProduct as any)?.isNew === true}
                disableAddNew={(selectedProduct as any)?.isNew === true}
              />
            </Modal>

            <Modal
              centered
              open={showDecommissionModal}
              title={
                decommissionProduct
                  ? `Decommission '${(decommissionProduct as any).name}'`
                  : "Decommission"
              }
              onCancel={resetDecommissionModal}
              footer={[
                <Button
                  key="cancel"
                  onClick={resetDecommissionModal}
                  disabled={isDecommissionSaving}
                >
                  Cancel
                </Button>,
                <Button
                  key="confirm"
                  type="primary"
                  onClick={handleDecommissionConfirm}
                  loading={isDecommissionSaving}
                >
                  Confirm
                </Button>,
              ]}
            >
              <Form layout="vertical">
                <Form.Item label="Product">
                  <Input
                    value={(decommissionProduct as any)?.name ?? ""}
                    disabled
                  />
                </Form.Item>
                <Form.Item
                  label="Decommission date"
                  required
                  validateStatus={decommissionError ? "error" : undefined}
                  help={decommissionError}
                >
                  <DatePicker
                    value={decommissionDate}
                    format={DateHelper.DATE_PICKER_FORMATS}
                    placeholder="dd-mmm-yyyy"
                    onChange={(value) => {
                      setDecommissionDate(value);
                      if (decommissionError) setDecommissionError(undefined);
                    }}
                    disabledDate={disableDecommissionDate}
                    style={{ width: "100%" }}
                    allowClear
                  />
                </Form.Item>
              </Form>
            </Modal>

            <Modal
              centered
              open={showRecommissionModal}
              title={
                recommissionProduct
                  ? `Recommission '${(recommissionProduct as any).name}'`
                  : "Recommission"
              }
              onCancel={resetRecommissionModal}
              footer={[
                <Button
                  key="cancel"
                  onClick={resetRecommissionModal}
                  disabled={isRecommissionSaving}
                >
                  Cancel
                </Button>,
                <Button
                  key="confirm"
                  type="primary"
                  onClick={handleRecommissionConfirm}
                  loading={isRecommissionSaving}
                >
                  Confirm
                </Button>,
              ]}
            >
              <Form layout="vertical">
                <Form.Item label="Product">
                  <Input
                    value={(recommissionProduct as any)?.name ?? ""}
                    disabled
                  />
                </Form.Item>
                <Alert
                  type="info"
                  showIcon
                  title="This action will make the product active again."
                />
              </Form>
            </Modal>

            <Modal
              centered
              open={showExcludeModal}
              title={
                excludeProduct
                  ? `Exclude '${(excludeProduct as any).name}'`
                  : "Exclude Booking Systems"
              }
              onCancel={resetExcludeModal}
              footer={[
                <Button
                  key="cancel"
                  onClick={resetExcludeModal}
                  disabled={isExcludeSaving}
                >
                  Cancel
                </Button>,
                <Button
                  key="save"
                  type="primary"
                  onClick={handleExcludeConfirm}
                  loading={isExcludeSaving}
                  disabled={
                    excludeLoading ||
                    excludeBookingSystemIdsEqual(
                      selectedExcludeSystems,
                      baselineExcludeSystems
                    )
                  }
                >
                  Save
                </Button>,
              ]}
            >
              {bookingSystemsLoading || excludeLoading ? (
                <div style={{ textAlign: "center", padding: "1rem" }}>
                  <Spin />
                </div>
              ) : (
                <>
                  {bookingSystemsError && (
                    <Alert
                      title="Failed to load booking systems"
                      description={bookingSystemsError}
                      type="error"
                      showIcon
                      style={{ marginBottom: 12 }}
                    />
                  )}
                  {bookingSystems.length === 0 && !bookingSystemsError ? (
                    <Alert
                      title="No booking systems available."
                      type="info"
                      showIcon
                    />
                  ) : (
                    <Form layout="vertical">
                      <Form.Item label="Exclude from booking systems">
                        <Checkbox.Group
                          value={selectedExcludeSystems}
                          onChange={(values) =>
                            setSelectedExcludeSystems(
                              (values as (string | number)[]).map((id) =>
                                String(id)
                              )
                            )
                          }
                        >
                          <Space orientation="vertical">
                            {bookingSystems.map((system: any) => (
                              <Checkbox
                                key={system.id}
                                value={String(system.id)}
                              >
                                {system.name}
                              </Checkbox>
                            ))}
                          </Space>
                        </Checkbox.Group>
                      </Form.Item>
                    </Form>
                  )}
                </>
              )}
            </Modal>

            <BreakdownsModal
              open={breakdownsModal.isOpen}
              title={breakdownsModal.title}
              items={breakdownsModal.items}
              colors={breakdownsModal.colors}
              total={breakdownsModal.total}
              hoverKey={breakdownsModal.hoverKey}
              onHoverKeyChange={breakdownsModal.setHoverKey}
              tooltipByKey={breakdownsModal.tooltipByKey}
              onClose={breakdownsModal.close}
            />

            <Modal
              title={`Add ${selectedProductType}`}
              open={addModalVisible}
              onCancel={() => {
                setAddModalVisible(false);
                setSelectedProductId(undefined);
              }}
              onOk={handleAddProduct}
              okText="Add"
              okButtonProps={{
                disabled: !selectedProductId || addProductLoading,
              }}
              destroyOnHidden
              width={600}
            >
              <Space
                orientation="vertical"
                style={{ width: "100%" }}
                size="large"
              >
                <div>
                  <Text strong>Select {selectedProductType} Product</Text>
                  <Select
                    value={selectedProductId}
                    placeholder={`Select a ${selectedProductType.toLowerCase()} product to add`}
                    style={{ width: "100%", marginTop: 8 }}
                    loading={addProductLoading}
                    showSearch={{ optionFilterProp: 'label' }}
                    onChange={(value) => setSelectedProductId(value as number)}
                    options={availableProducts.map((product) => ({
                      label: product.name,
                      value: product.id,
                    }))}
                    notFoundContent={
                      addProductLoading ? (
                        <Spin size="small" />
                      ) : (
                        <Text type="secondary">No available products</Text>
                      )
                    }
                  />
                  {availableProducts.length === 0 && !addProductLoading && (
                    <Text
                      type="secondary"
                      style={{ display: "block", marginTop: 8 }}
                    >
                      All {selectedProductType.toLowerCase()} products are
                      already in this pricelist, or no active products are
                      available.
                    </Text>
                  )}
                </div>
              </Space>
            </Modal>

            <EditModeProvider value={editModeContextValue}>
            <FormErrorsProvider value={formErrors ?? EMPTY_FORM_ERRORS}>
            <Table<FlatRow>
                columns={columns}
                dataSource={flatRows}
                pagination={false}
                bordered
                size="small"
                rowClassName={tableRowClassName}
                onRow={tableRowEvents}
                className="products-table"
                tableLayout="fixed"
                scroll={{ x: "max-content" }}
                sticky
            />
            </FormErrorsProvider>
            </EditModeProvider>
          </>
        );
      }

      return (
        <div style={{ textAlign: "center", padding: "2em" }}>
          <Spin size="large" />
        </div>
      );
    }

    return (
      <>
        {!selectedCentreCode ? (
          <Alert
            title="Select a centre"
            description="Please select a centre from the left hand side menu."
            type="info"
          />
        ) : (
          renderTable()
        )}
      </>
    );
  }
);

ProductTable.displayName = "ProductTable";

export default ProductTable;